/**
 * GoogleAnalytics loader — fetches ga4_measurement_id + gsc_verification from
 * /api/seo/config on mount, then:
 *   1. Injects gtag.js script when a GA4 ID is present.
 *   2. Sends a `page_view` on every route change.
 *   3. Injects a Google Search Console verification <meta> tag.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const injectGA = (id) => {
  if (!id || window.__GA_LOADED === id) return;
  window.__GA_LOADED = id;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, { send_page_view: false });
};

const injectGSC = (token) => {
  if (!token) return;
  let el = document.head.querySelector('meta[name="google-site-verification"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'google-site-verification');
    document.head.appendChild(el);
  }
  el.setAttribute('content', token);
};

const GoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/seo/config`)
      .then((r) => {
        if (cancelled) return;
        const cfg = r.data || {};
        if (cfg.ga4_measurement_id) {
          window.__GA_ID = cfg.ga4_measurement_id;
          injectGA(cfg.ga4_measurement_id);
        }
        if (cfg.gsc_verification) injectGSC(cfg.gsc_verification);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!window.gtag || !window.__GA_ID) return;
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search,
      send_to: window.__GA_ID,
    });
  }, [location.pathname, location.search]);

  return null;
};

export default GoogleAnalytics;
